import { zodResolver } from "@hookform/resolvers/zod";
import { Building2 } from "lucide-react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router";
import { z } from "zod";

import useApiRequest, { ApiResponse } from "@/lib/hooks/use-request";
import {
  CreateOrganizationRequestDto,
  OrganizationDetailsResponseDto,
  OrganizationResponseDto,
  UpdateOrganizationRequestDto,
} from "@/lib/interfaces/organisations";
import { Button } from "@/ui-components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/ui-components/ui/form";
import { Input } from "@/ui-components/ui/input";

const OrganisationEditor = ({
  item,
  onSubmitDone,
  withoutRedirect,
  initialText,
  belongsToPipelineId,
}: {
  item?: OrganizationDetailsResponseDto;
  onSubmitDone?: (id: string, title: string) => void;
  withoutRedirect?: boolean;
  initialText?: string;
  belongsToPipelineId?: string;
}) => {
  const { apiRequest } = useApiRequest();
  const navigate = useNavigate();

  const organizationSchema = z.object({
    name: z
      .string()
      .min(1, { message: "Name is required" })
      .max(100, { message: "Name must not exceed 100 characters" }),
    address: z.string().max(200, { message: "Address must not exceed 200 characters" }).optional().or(z.literal("")),
  });

  type OrganizationFormSchema = z.infer<typeof organizationSchema>;

  const form = useForm<OrganizationFormSchema>({
    resolver: zodResolver(organizationSchema),
    defaultValues: {
      name: item?.name || initialText || "",
      address: item?.address || "",
    },
  });

  const onSubmit = async (data: OrganizationFormSchema) => {
    let res: ApiResponse<OrganizationResponseDto>;
    if (item) {
      // Update existing organization
      const req: UpdateOrganizationRequestDto = {
        name: data.name !== item.name ? data.name : undefined,
        address: data.address !== item.address ? data.address : undefined,
      };

      res = await apiRequest(`contacts/organizations/${item._id}`, "PATCH", {
        body: req,
        toast: { toastText: "Organization updated successfully" },
      });
    } else {
      // Create new organization
      const req: CreateOrganizationRequestDto = {
        name: data.name,
        ...(data.address && { address: data.address }),
        ...(belongsToPipelineId && { belongsToPipelineIds: [belongsToPipelineId] }),
      };

      res = await apiRequest("contacts/organizations", "POST", {
        body: req,
        toast: { toastText: "Organization created successfully" },
      });
    }

    if (res.data) {
      if (onSubmitDone) {
        onSubmitDone(res.data._id, res.data.name);
      }
      if (!withoutRedirect) {
        navigate(`/contacts/organisations/${res.data._id}`);
      }
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-5 h-full">
        <div className="flex flex-col gap-2">
          <FormLabel className="text-[16px] font-bold">Organization Name:*</FormLabel>
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem className="flex flex-col bg-white rounded-lg">
                <FormControl>
                  <Input {...field} placeholder="Organization Name" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        <div className="flex flex-col gap-2">
          <FormLabel className="text-[16px] font-bold">Address:</FormLabel>
          <FormField
            control={form.control}
            name="address"
            render={({ field }) => (
              <FormItem className="flex flex-col bg-white rounded-lg">
                <FormControl>
                  <Input {...field} placeholder="Address" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        <Button type="submit" variant="default" className="text-white font-bold flex items-center gap-4 w-fit self-end">
          <Building2 className="h-4 w-4" />
          <span>{item ? "Organisation aktualisieren" : "Organisation erstellen"}</span>
        </Button>
      </form>
    </Form>
  );
};

export default OrganisationEditor;
